import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { cn } from '../../../lib/cn';
import { contactSchema, type CompanyContactRow, type ContactInput } from './contacts.schema';
import styles from './contacts.module.css';

const DEFAULT_VALUES: ContactInput = {
  full_name: '',
  role_title: '',
  email: '',
  phone: '',
  linkedin_url: '',
  notes: '',
};

function valuesFromContact(contact: CompanyContactRow | null): ContactInput {
  if (!contact) return DEFAULT_VALUES;
  return {
    full_name: contact.full_name,
    role_title: contact.role_title ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    linkedin_url: contact.linkedin_url ?? '',
    notes: contact.notes ?? '',
  };
}

interface ContactFormProps {
  contact: CompanyContactRow | null;
  formId: string;
  disabled?: boolean;
  onSubmit: (values: ContactInput) => void | Promise<void>;
}

export function ContactForm({ contact, formId, disabled = false, onSubmit }: ContactFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(valuesFromContact(contact));
  }, [contact, reset]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.field}>
        <label htmlFor="contact-full-name">שם מלא</label>
        <input
          id="contact-full-name"
          type="text"
          className={cn(styles.input, errors.full_name && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('full_name')}
        />
        {errors.full_name ? <p className={styles.error}>{errors.full_name.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="contact-role-title">תפקיד</label>
        <input
          id="contact-role-title"
          type="text"
          className={cn(styles.input, errors.role_title && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('role_title')}
        />
        {errors.role_title ? <p className={styles.error}>{errors.role_title.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="contact-email">אימייל</label>
        <input
          id="contact-email"
          type="email"
          dir="ltr"
          className={cn(styles.input, errors.email && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('email')}
        />
        {errors.email ? <p className={styles.error}>{errors.email.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="contact-phone">טלפון</label>
        <input
          id="contact-phone"
          type="tel"
          dir="ltr"
          className={styles.input}
          disabled={disabled || isSubmitting}
          {...register('phone')}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="contact-linkedin">LinkedIn</label>
        <input
          id="contact-linkedin"
          type="url"
          dir="ltr"
          className={cn(styles.input, errors.linkedin_url && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('linkedin_url')}
        />
        {errors.linkedin_url ? <p className={styles.error}>{errors.linkedin_url.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="contact-notes">הערות</label>
        <textarea
          id="contact-notes"
          rows={5}
          className={cn(styles.input, styles.textarea, errors.notes && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('notes')}
        />
        {errors.notes ? <p className={styles.error}>{errors.notes.message}</p> : null}
      </div>
    </form>
  );
}
